import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency } from "@/utils/formatCurrency";
import { DrawdownSummary } from "@trader-dashboard/shared";
import { AlertTriangle } from "lucide-react";

type DrawdownSummaryCardsProps = {
  summary: DrawdownSummary;
  referencePips: number;
};

const Metric = ({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "danger" | "warning";
}): React.ReactNode => (
  <Card>
    <CardContent className="p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`mt-1 text-2xl font-bold tabular-nums ${
          tone === "danger" ? "text-red-600" : tone === "warning" ? "text-amber-600" : ""
        }`}
      >
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </CardContent>
  </Card>
);

export const DrawdownSummaryCards = ({
  summary,
  referencePips,
}: DrawdownSummaryCardsProps): React.ReactNode => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <Metric
      label="Open positions"
      value={`${summary.openPositions}`}
      sub={`${summary.totalLots.toFixed(2)} lots total`}
    />
    <Metric
      label="Floating P/L"
      value={formatCurrency(summary.floatingProfit)}
      sub="Including swap and commission"
      tone={summary.floatingProfit < 0 ? "danger" : undefined}
    />
    <Metric
      label={`Loss at ${referencePips} pips`}
      value={summary.scenarioLoss === null ? "—" : formatCurrency(summary.scenarioLoss)}
      sub={
        summary.scenarioLossPct === null
          ? "Adverse move on every position"
          : `${summary.scenarioLossPct.toFixed(1)}% of equity`
      }
      tone="danger"
    />
    <Metric
      label="Worst case at stops"
      value={summary.stopLoss === null ? "Unbounded" : formatCurrency(summary.stopLoss)}
      sub={
        summary.ordersWithoutStop > 0
          ? `${summary.ordersWithoutStop} order${summary.ordersWithoutStop === 1 ? "" : "s"} without a stop loss`
          : summary.stopLossPct === null
            ? "All positions have a stop"
            : `${summary.stopLossPct.toFixed(1)}% of equity`
      }
      tone={summary.ordersWithoutStop > 0 ? "warning" : undefined}
    />
  </div>
);

export const UnpricedPairsWarning = ({ pairs }: { pairs: string[] }): React.ReactNode => {
  if (pairs.length === 0) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        Excluded from the estimate — not enough closed trades to derive a value per pip:{" "}
        <span className="font-medium">{pairs.join(", ")}</span>
      </span>
    </div>
  );
};
